import { Col } from "@shared/antd/col";
import { Row } from "@shared/antd/row";
import { Form } from "@shared/antd/form";
import { Button } from "@shared/antd/button";
import { Select } from "@shared/antd/select";
import { Modal } from "@shared/antd/modal";
import { Icon } from "@shared/antd/icon";
import React, { createElement, Fragment, useState } from "react";
import { FormComponentProps } from "antd/lib/form";
import { useApplicationStore } from "app/entities/form-controller/abstract";
import Notification from "../../../util/Notification/Notification";
import { restServices } from "cuba/services";
import { rootStore } from "../../../store";
import { FormattedMessage, useIntl } from "react-intl";
import CustomButton, { ButtonType } from "../../../components/Button/Button";
import { SerializedEntity } from "@cuba-platform/rest";
import { DicHrRole } from "cuba/entities/base/tsadv$DicHrRole";
import { TsadvUser } from "cuba/entities/base/tsadv$UserExt";
import { NotPersisitBprocActors } from "cuba/entities/base/tsadv_NotPersisitBprocActors";
import Candidate from "app/pages/Bproc/component/Candidate";
import { TextArea } from "app/shared/antd/text-area";
import { ApplicationState } from "app/entities/form-controller/model/store";
import { useHistory } from "react-router-dom";
import { goBackOrHomePage } from "../../../util";
import { TableAdaptive } from "../../../common";
import { ColumnProps } from "antd/lib/table";
import { CustomSelectedActors } from "app/pages/Bproc/modal/StartBprocModal";

type StartButtonProps<T> = FormComponentProps;

const isStartCommentVisible = false;
const START_OUTCOME = "START";

export const StartButton = <T extends object = any>(
  props: StartButtonProps<T>,
) => {
  const { form } = props;
  const store = useApplicationStore<ApplicationState<T>>();

  const getUpdatedInstance = store((s) => s.getUpdatedInstance);
  const employeePersonGroupId = store((s) => s.employeePersonGroupId);
  const bpmStore = store((s) => s.bpmStore((bpm) => bpm));
  const validate = store((s) => s.validate);
  const dataInstance = store((s) => s.dataInstance);
  const updateAndCommit = store((s) => s.updateAndCommit);
  const commentRequiredOutcomes = store((s) => s.commentRequiredOutcomes);

  const modalStoreState = bpmStore.modalStore((m) => m);
  const { bprocRolesDefiner, notPersisitBprocActorsFiltered } = bpmStore;

  const { beforeCompletePredicate } = modalStoreState;
  const intl = useIntl();
  const history = useHistory();

  const [selectedHrRole, setSelectedHrRole] = useState<DicHrRole | null>(null);
  const [selectedUser, setSelectedUser] = useState<TsadvUser | null>(null);

  const [usersState, setUsersState] = useState<{
    users: TsadvUser[];
    isUserLoading: boolean;
    lastSendDate: number;
  }>({ users: [], isUserLoading: false, lastSendDate: 0 });

  const [customSelectedActors, setCustomSelectedActors] = useState<
    CustomSelectedActors[]
  >([]);

  const showModalOrMessage = async (e: any) => {
    e.preventDefault();

    const isValid = validate ? await validate() : true;
    if (!isValid) return;

    //TODO add action before Start
    await updateAndCommit?.();
    const loadBpmRolesDefiner = async () => {
      const request = getUpdatedInstance(form);
      try {
        const roles = await bpmStore.loadBprocRolesDefiner(
          request,
          employeePersonGroupId,
        );
        const notPersisitActors = await bpmStore.loadNotPersisitBprocActors(
          request,
          roles,
          employeePersonGroupId,
        );
      } catch (error) {
        Notification.error({ message: (error as Error).message });
        modalStoreState.closeModal(START_OUTCOME);
      }
    };

    try {
      if (beforeCompletePredicate) {
        const predicateResult = await beforeCompletePredicate(START_OUTCOME);
        if (!predicateResult) return;
      }

      modalStoreState.openModal(START_OUTCOME);
      await loadBpmRolesDefiner();
    } catch (error) {
      modalStoreState.closeModal(START_OUTCOME);
      Notification.error({ message: error.message });
    }
  };

  const normalizeBprocActors = () => {
    return notPersisitBprocActorsFiltered.map((actor) => {
      const currentTaskCode = actor?.hrRole?.code;
      const changedActor = customSelectedActors.find(
        (item) => item.currentTaskCode === currentTaskCode,
      );

      // если не найдено — вернуть без изменений
      if (!changedActor) return actor;

      const selectedApprovers = changedActor.selectedApprovers;

      // если selectedApprovers пустой — вернуть без изменений
      if (!selectedApprovers || selectedApprovers.length === 0) return actor;

      // иначе заменить users новым списком
      return {
        ...actor,
        users: selectedApprovers,
      };
    });
  };

  const handleStartBrockSys = async () => {
    modalStoreState.setIsLoadingModal(true);
    const request = getUpdatedInstance(form);
    const res = normalizeBprocActors();

    try {
      await restServices.startBprocService.saveBprocActors({
        entityId: dataInstance?.id,
        notPersisitBprocActors: res,
      });

      await restServices.bprocRuntimeService.startProcessInstanceByKey({
        businessKey: bpmStore.processInstanceBusinessKey,
        processDefinitionKey: bpmStore.processDefinitionKey,
        variables: {
          entity: request,
          rolesLinks: bprocRolesDefiner?.links,
          startComment: form.getFieldValue("bproc-comment"),
          employeePersonGroupId: employeePersonGroupId,
          isAssistant: rootStore.assistantTeamInfo.active,
        },
      });

      modalStoreState.closeModal(START_OUTCOME);

      Notification.success({
        message: intl.formatMessage({ id: "START.success" }),
      });
    } catch (e) {
      Notification.error({
        message: intl.formatMessage({ id: "management.editor.error" }),
      });
    } finally {
      modalStoreState.setIsLoadingModal(false);
    }
  };

  const onChangeBprocRole = (
    value: string,
    option: React.ReactElement<HTMLLIElement>,
  ) => {
    if (!value) {
      setSelectedHrRole(null);
      return;
    }
    const foundRole =
      bprocRolesDefiner?.links
        ?.map((link) => link.hrRole)
        .find((hrRole) => hrRole.id === value) || null;
    setSelectedHrRole(foundRole);
  };

  const onSearchUser = async (value: string) => {
    if (value.trim().length < 3) return;

    const sendDate = Date.now();
    setUsersState((prev) => ({ ...prev, isUserLoading: true }));

    try {
      const users = await restServices.userService.findUsers(
        value,
        "portal-bproc-users",
      );

      setUsersState((prev) => {
        if (sendDate > prev.lastSendDate) {
          return { ...prev, users, lastSendDate: sendDate };
        }
        return prev;
      });
    } finally {
      setUsersState((prev) => ({ ...prev, isUserLoading: false }));
    }
  };

  const onChangeUser = (
    value: string,
    option: React.ReactElement<HTMLLIElement>,
  ) => {
    setSelectedUser(
      value
        ? {
            id: value,
            fullNameWithLogin: option.props.children as any,
          }
        : null,
    );
  };

  const addBprocUser = () => {
    const userAlreadyExists = notPersisitBprocActorsFiltered.some((actor) =>
      (actor.users as TsadvUser[]).some((u) => u.id === selectedUser?.id),
    );

    if (userAlreadyExists) {
      Notification.error({
        message: intl.formatMessage({ id: "bproc.startBproc.modal.error" }),
      });
      return;
    }

    const rolesLink = bprocRolesDefiner?.links?.find(
      (link) => link.hrRole?.id === selectedHrRole?.id,
    );

    notPersisitBprocActorsFiltered.unshift({
      hrRole: selectedHrRole,
      users: [selectedUser],
      bprocUserTaskCode: rolesLink?.bprocUserTaskCode,
      rolesLink,
    } as any);
  };

  const deleteRow = (row: SerializedEntity<NotPersisitBprocActors>) => {
    return notPersisitBprocActorsFiltered.filter(
      (r) => r.users![0].id !== row.users![0].id,
    );
  };

  const showDeletionDialog = (e: SerializedEntity<NotPersisitBprocActors>) => {
    Modal.confirm({
      title: intl.formatMessage(
        { id: "management.browser.delete.areYouSure" },
        { instanceName: e._instanceName },
      ),
      okText: intl.formatMessage({ id: "management.browser.delete.ok" }),
      cancelText: intl.formatMessage({
        id: "management.browser.delete.cancel",
      }),
      onOk: () => {
        return deleteRow(e);
      },
    });
  };

  const commentValidator = (rule: any, value: any, callback: any) => {
    if (
      modalStoreState.isModalOpen(START_OUTCOME) &&
      !value &&
      isStartCommentVisible &&
      commentRequiredOutcomes?.find((outcome) => outcome === START_OUTCOME)
    ) {
      callback(intl.formatMessage({ id: "comment.required" }));
    }
    callback();
  };

  const validateActors = (): boolean => {
    const missingRole = bprocRolesDefiner?.links
      .filter((link) => link.required && link.isAddableApprover)
      .find((link) =>
        notPersisitBprocActorsFiltered.some(
          (actor) => actor.bprocUserTaskCode === link.bprocUserTaskCode,
        ),
      );

    if (missingRole) {
      Notification.error({
        message: intl.formatMessage(
          { id: "add.required.actor" },
          { actor: missingRole.hrRole?._instanceName },
        ),
      });
      return false;
    }

    return true;
  };

  const handleOk = async (e: any) => {
    if (!validateActors()) return;

    const confirmAction = async () => {
      try {
        modalStoreState.setIsLoadingModal(true);

        await handleStartBrockSys();

        goBackOrHomePage(history, true);
      } catch (error) {
        Notification.error({
          message: intl.formatMessage({ id: "management.editor.error" }),
        });
      } finally {
        modalStoreState.setIsLoadingModal(false);
      }
    };

    const showConfirm = () => {
      Modal.confirm({
        title: intl.formatMessage({ id: "START.INFO" }),
        okText: intl.formatMessage({ id: "cubaReact.dataTable.yes" }),
        cancelText: intl.formatMessage({ id: "cubaReact.dataTable.no" }),
        onOk: () => confirmAction(),
      });
    };

    if (isStartCommentVisible) {
      try {
        form.validateFields(["bproc-comment"], { force: true });
        showConfirm();
      } catch {
        Notification.error({
          message: intl.formatMessage({
            id: "management.editor.validationError",
          }),
        });
      }
    } else {
      showConfirm();
    }
  };

  const changeCustomSelectedActors = (
    currentTaskCode: string,
    selectedActors: TsadvUser[],
  ) => {
    setCustomSelectedActors((prev) => {
      const exists = prev.some(
        (item) => item.currentTaskCode === currentTaskCode,
      );

      if (exists) {
        return prev.map((item) =>
          item.currentTaskCode === currentTaskCode
            ? { ...item, selectedApprovers: selectedActors }
            : item,
        );
      } else {
        return [
          ...prev,
          { currentTaskCode, selectedApprovers: selectedActors },
        ];
      }
    });
  };

  const columns: ColumnProps<SerializedEntity<NotPersisitBprocActors>>[] = [
    {
      key: "role",
      title: <FormattedMessage id="bproc.startBproc.modal.roles" />,
      render: (_text: any, record: NotPersisitBprocActors) =>
        (record.hrRole! as SerializedEntity<DicHrRole>)._instanceName,
    },
    {
      key: "candidates",
      title: <FormattedMessage id="bproc.startBproc.modal.users" />,
      render: (_text: any, record: NotPersisitBprocActors, index) => (
        <Candidate
          candidates={record?.users || []}
          isCanBeSelected={
            record?.rolesLink?.canSelectNextApprover && index === 0
          }
          currentTaskCode={record?.hrRole?.code}
          changeCustomSelectedActors={changeCustomSelectedActors}
          customSelectedActors={customSelectedActors}
        />
      ),
    },
    {
      key: "action",
      render: (_text: any, record: SerializedEntity<NotPersisitBprocActors>) =>
        record.isSystemRecord ? null : (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => showDeletionDialog(record)}
          >
            <Icon type="delete" style={{ fontSize: 18, cursor: "pointer" }} />
          </Button>
        ),
    },
  ];
  return (
    <div>
      <CustomButton
        buttonType={ButtonType.PRIMARY}
        onClick={(e) => showModalOrMessage(e)}
        key="start"
      >
        {intl.formatMessage({
          id: START_OUTCOME,
        })}
      </CustomButton>

      <Modal
        title={intl.formatMessage({ id: START_OUTCOME })}
        visible={modalStoreState.isModalOpen(START_OUTCOME)}
        confirmLoading={modalStoreState.isLoadingModal}
        onOk={handleOk}
        width={"60vw"}
        onCancel={() => modalStoreState.closeModal(START_OUTCOME)}
      >
        <div>
          <div>
            <Row type={"flex"} justify={"center"} align={"bottom"} gutter={10}>
              <Col xs={6}>
                <Form.Item
                  style={{ margin: 0 }}
                  label={<FormattedMessage id="bproc.startBproc.modal.role" />}
                >
                  <Select
                    style={{ width: "100%" }}
                    onChange={onChangeBprocRole}
                    allowClear
                  >
                    {bprocRolesDefiner?.links?.length ? (
                      bprocRolesDefiner.links
                        .filter((link) => link.isAddableApprover && link.hrRole)
                        .map(({ hrRole }) => (
                          <Select.Option key={hrRole?.id} value={hrRole?.id}>
                            {hrRole?._instanceName}
                          </Select.Option>
                        ))
                    ) : (
                      <Select.Option key="empty" />
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={15}>
                <Form.Item
                  style={{ margin: 0 }}
                  label={<FormattedMessage id="bproc.startBproc.modal.user" />}
                >
                  <Select
                    style={{ width: "100%" }}
                    filterOption={false}
                    allowClear
                    showSearch
                    loading={usersState.isUserLoading}
                    onSearch={onSearchUser}
                    onChange={onChangeUser}
                  >
                    {usersState.users.length > 0
                      ? usersState.users.map(({ id, fullNameWithLogin }) => (
                          <Select.Option key={id} title={fullNameWithLogin}>
                            {fullNameWithLogin}
                          </Select.Option>
                        ))
                      : null}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={3}>
                <Form.Item style={{ margin: 0 }}>
                  <Button
                    type={"primary"}
                    block
                    disabled={!(selectedHrRole && selectedUser)}
                    onClick={addBprocUser}
                  >
                    <FormattedMessage id="bproc.startBproc.modal.add" />
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>

          <TableAdaptive<NotPersisitBprocActors>
            isNeedMobile
            dataSource={Array.from(notPersisitBprocActorsFiltered || [])}
            pagination={false}
            showHeader={true}
            loading={!notPersisitBprocActorsFiltered}
            columns={columns}
            rowKey={(record) => record.id}
          />

          <div style={isStartCommentVisible ? {} : { display: "none" }}>
            <Form.Item
              label={createElement(
                Fragment,
                null,
                intl.formatMessage({ id: "comment" }),
              )}
            >
              {form.getFieldDecorator("bproc-comment", {
                rules: [
                  {
                    required: !!(commentRequiredOutcomes || []).find(
                      (outcome) => outcome === START_OUTCOME,
                    ),
                    validator: commentValidator,
                  },
                ],
              })(<TextArea rows={4} />)}
            </Form.Item>
          </div>
        </div>
      </Modal>
    </div>
  );
};
